/**
 * pdfExport.js
 * High-DPI A4 PDF export using html2canvas (scale:3) + jsPDF.
 *
 * Key features:
 *  1. High DPI rendering (scale: 3 for 300 DPI clarity).
 *  2. Pre-capture overflow & clamp removal so 100% of text/code is rendered.
 *  3. Applies .pdf-export-mode class calibrated to physical A4 typography (14pt title, 12pt body).
 *  4. Enforces break-inside: avoid to prevent card clipping.
 *  5. Re-restores all original inline styles after capture.
 */

/** ─── Page range parser ──────────────────────────────────────────────────── */
export function parsePageRange(rangeStr, totalPages) {
  if (!rangeStr || rangeStr.trim().toLowerCase() === 'all') {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const indices = new Set();
  const parts = rangeStr.split(',').map(s => s.trim()).filter(Boolean);

  for (const part of parts) {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(s => parseInt(s.trim(), 10));
      if (!isNaN(start) && !isNaN(end)) {
        for (let i = start; i <= end; i++) {
          const idx = i - 1;
          if (idx >= 0 && idx < totalPages) indices.add(idx);
        }
      }
    } else {
      const num = parseInt(part, 10);
      if (!isNaN(num)) {
        const idx = num - 1;
        if (idx >= 0 && idx < totalPages) indices.add(idx);
      }
    }
  }

  return Array.from(indices).sort((a, b) => a - b);
}

/** ─── Override collector ──────────────────────────────────────────────────── */
function expandForCapture(el) {
  const records = [];
  const allEls = [el, ...el.querySelectorAll('*')];

  allEls.forEach(elem => {
    const cs = window.getComputedStyle(elem);

    const needsFix =
      cs.overflow       === 'hidden'  ||
      cs.overflowY      === 'hidden'  || cs.overflowY === 'auto'  || cs.overflowY === 'scroll' ||
      cs.overflowX      === 'hidden'  || cs.overflowX === 'auto'  || cs.overflowX === 'scroll' ||
      (cs.maxHeight !== 'none' && cs.maxHeight !== '' && cs.maxHeight !== '0px') ||
      cs.webkitLineClamp !== 'none'   ||
      cs.display         === '-webkit-box';

    if (!needsFix) return;

    const orig = {
      overflow:          elem.style.overflow,
      overflowY:         elem.style.overflowY,
      overflowX:         elem.style.overflowX,
      maxHeight:         elem.style.maxHeight,
      height:            elem.style.height,
      display:           elem.style.display,
      webkitLineClamp:   elem.style.webkitLineClamp,
      webkitBoxOrient:   elem.style.webkitBoxOrient,
    };

    elem.style.overflow        = 'visible';
    elem.style.overflowY       = 'visible';
    elem.style.overflowX       = 'visible';
    elem.style.maxHeight       = 'none';
    elem.style.height          = 'auto';
    elem.style.webkitLineClamp = 'unset';

    if (cs.display === '-webkit-box') {
      elem.style.display       = 'block';
    }

    records.push({ elem, orig });
  });

  return records;
}

function restoreAfterCapture(records) {
  records.forEach(({ elem, orig }) => {
    elem.style.overflow        = orig.overflow;
    elem.style.overflowY       = orig.overflowY;
    elem.style.overflowX       = orig.overflowX;
    elem.style.maxHeight       = orig.maxHeight;
    elem.style.height          = orig.height;
    elem.style.display         = orig.display;
    elem.style.webkitLineClamp = orig.webkitLineClamp;
    elem.style.webkitBoxOrient = orig.webkitBoxOrient;
  });
}

/** ─── Main export function ────────────────────────────────────────────────── */
export async function exportToPDF(
  sheetRefs,
  pageIndices,
  filename = 'DataScience-Notes.pdf',
  onProgress
) {
  const [html2canvasModule, jsPDFModule] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const html2canvas = html2canvasModule.default;
  const { jsPDF }   = jsPDFModule;

  const A4_W_MM = 210;
  const A4_H_MM = 297;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    putOnlyUsedFonts: true,
    compress: true,
  });

  for (let i = 0; i < pageIndices.length; i++) {
    const idx = pageIndices[i];
    const el  = sheetRefs[idx];
    if (!el) continue;

    onProgress?.(i + 1, pageIndices.length);

    // 1. Strip UI chrome & apply export typography
    el.classList.add('pdf-export-mode');

    // 2. Expand all overflow/clamp constraints
    const restoreRecords = expandForCapture(el);

    // Wait for browser reflow
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      // 3. High DPI capture (scale: 3 = 300 DPI equivalent)
      const canvas = await html2canvas(el, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#FFFFFE',
        logging: false,
        letterRendering: true,
        windowWidth:  el.scrollWidth,
        windowHeight: el.scrollHeight,
        ignoreElements: elem =>
          elem.classList.contains('no-print') ||
          elem.dataset.noPrint === 'true',
        onclone: (clonedDoc, clonedEl) => {
          clonedEl.style.webkitFontSmoothing = 'antialiased';
          clonedEl.style.mozOsxFontSmoothing = 'grayscale';
          clonedEl.style.textRendering       = 'optimizeLegibility';
        },
      });

      const imgData = canvas.toDataURL('image/png', 1.0);

      if (i > 0) pdf.addPage('a4', 'portrait');

      pdf.addImage(imgData, 'PNG', 0, 0, A4_W_MM, A4_H_MM, undefined, 'FAST');
    } finally {
      restoreAfterCapture(restoreRecords);
      el.classList.remove('pdf-export-mode');
    }
  }

  pdf.save(filename);
}
